import { AccountStore } from '@/modules/account/accountStore';
import { ErrorStore } from '@/modules/error/errorStore';
import { RootStore } from '@/store';
import { delay, getBackendUrl } from '@/utils';
import { isB3LauncherApp } from '@/utils/isB3LauncherApp';
import { isTelegramApp } from '@/utils/isTelegramApp';
import { storage } from '@/utils/storage';
import algosdk from 'algosdk';
import type { AxiosError, Method } from 'axios';
import axios from 'axios';
import axiosRetry from 'axios-retry';

type ERROR = {
  content: {
    message?: string;
  };
  error: string;
};

interface RequestConfig {
  path: string;
  method: Method;
  headers?: Record<string, string>;
  body?: any;
}

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
};

export const DO_AUTH = false;
if (import.meta.env.MODE === 'development' && DO_AUTH) {
  defaultHeaders.Authorization = import.meta.env.VITE_DEV_ACCESS_TOKEN;
}

export function initApi() {
  axiosInstance.defaults.baseURL = getBackendUrl();
}

export const axiosInstance = axios.create();

export function setupAxiosRetry(store: RootStore) {
  axiosRetry(axiosInstance, {
    retries: 2,
    retryDelay: () => 1000,
    onRetry: async (_retryCount, error, requestConfig) => {
      const err = (error as AxiosError<ERROR>)?.response?.data?.error;
      if (
        (err === 'AuthenticationError' ||
          err === 'JsonWebTokenError' ||
          err === 'TokenExpiredError') &&
        requestConfig.url !== '/v1/account/data'
      ) {
        await storage.removeItem('token');
        if (!isB3LauncherApp()) {
          if (isTelegramApp()) await activateAuthToken(store.accountStore);
          else await refreshGoogleToken(store.accountStore);
        }
        await store.accountStore.getAccountData();
      }
    },
  });
}

export const apiRequest = async (
  requests: RequestConfig[],
  errorStore?: ErrorStore,
): Promise<any[]> => {
  try {
    const axiosRequests = requests.map(
      ({ path, method, headers = {}, body = undefined }) => {
        return axiosInstance({
          url: path,
          method,
          headers: {
            ...(body instanceof FormData ? {} : defaultHeaders),
            ...headers,
          } as Record<string, string>,
          data: body,
        });
      },
    );

    const responses = await Promise.all(axiosRequests);
    return responses.map((response) => response.data.content);
  } catch (error) {
    const errorMessage =
      (error as AxiosError<ERROR>)?.response?.data?.content?.message ||
      (error as AxiosError<{ content: string }>)?.response?.data?.content;
    if (errorStore && errorMessage && !requests[0].path.includes('balances')) {
      if (
        !(
          errorMessage === 'AuthenticationError' ||
          errorMessage === 'JsonWebTokenError' ||
          errorMessage === 'TokenExpiredError'
        )
      ) {
        errorStore.setError(errorMessage);
        console.error('Error when executing request:', errorMessage);
      }
    }
    return [
      {
        isError: (error as AxiosError<ERROR>)?.response?.data?.error,
        message: errorMessage,
        error,
      },
    ];
  }
};

export const refreshGoogleToken = async (accountStore: AccountStore) => {
  const [authToken] = await apiRequest([
    {
      path: '/v1/auth/google-oauth2-refresh',
      method: 'POST',
      body: {
        refresh_token: await storage.getItem('refresh_token'),
      },
    },
  ]);
  if (!authToken.isError) await accountStore.setAuthorizationToken(authToken);
};

export const activateAuthToken = async (accountStore: AccountStore) => {
  await delay(10);
  let initData = {};
  if (isTelegramApp()) initData = (window as any).Telegram?.WebApp?.initData;

  if (DO_AUTH && import.meta.env.MODE === 'development') {
    await accountStore.setAuthorizationToken(
      import.meta.env.VITE_DEV_ACCESS_TOKEN,
    );
    return;
  }

  const [authToken] = await apiRequest([
    {
      path: '/v1/auth',
      method: 'POST',
      headers: {
        'bypass-tunnel-reminder': 'true',
      },
      body: { initData },
    },
  ]);
  if (!authToken.isError) await accountStore.setAuthorizationToken(authToken);
};

export const activateAuthTokenByB3 = async (accountStore: AccountStore) => {
  const [authToken] = await apiRequest([
    {
      path: '/v1/auth/b3-oauth2',
      method: 'POST',
      headers: {
        'bypass-tunnel-reminder': 'true',
      },
      body: {
        token: new URLSearchParams(window.location.search).get('token'),
      },
    },
  ]);

  await accountStore.setAuthorizationToken(authToken.token);
};

export const activateAuthTokenByGoogle = async (
  code: string,
  accountStore: AccountStore,
) => {
  const [authToken] = await apiRequest([
    {
      path: '/v1/auth/google-oauth2',
      method: 'POST',
      headers: {
        'bypass-tunnel-reminder': 'true',
      },
      body: JSON.stringify({ code }),
    },
  ]);

  await storage.setItem('refresh_token', authToken.refresh_token);
  await accountStore.setAuthorizationToken(authToken.token);
};

// ========================================================================================================================
// ========================================================================================================================
// ========================================================================================================================

type ApiResp<T = any> = { isError?: boolean; message?: string } & T;

const toB64 = (u8: Uint8Array) =>
  typeof Buffer !== 'undefined'
    ? Buffer.from(u8).toString('base64')
    : btoa(String.fromCharCode(...u8));

function ensureAlgod(network: 'mainnet' | 'testnet' = 'mainnet') {
  const base =
    network === 'testnet'
      ? 'https://testnet-api.algonode.cloud'
      : 'https://mainnet-api.algonode.cloud';
  return new algosdk.Algodv2('', base, '');
}

export async function activateAuthTokenByAlgorand(
  algorandAddress: string,
  accountStore: { setAuthorizationToken: (t: string) => Promise<void> },
  walletFunctions: { signTransactions: any; signData?: any },
  opts?: {
    network?: 'mainnet' | 'testnet';
    domain?: string;
    abortSignal?: AbortSignal;
  },
) {
  const network = opts?.network ?? 'mainnet';
  const domain =
    opts?.domain ??
    (typeof window !== 'undefined' ? window.location.host : 'your-app');

  console.log('w0');
  // 1) Get nonce and timestamp from server to protect against replay attacks
  const [nonceResp] = (await apiRequest([
    {
      path: '/v1/auth/algorand/nonce',
      method: 'POST',
      body: { algorand_address: algorandAddress },
    },
  ])) as [ApiResp<{ nonce: string; timestamp: number }>];
  console.log('w1');

  if (!nonceResp || nonceResp.isError) {
    console.log('w2');
    throw new Error(nonceResp?.message || 'Failed to get nonce');
  }
  const { nonce, timestamp } = nonceResp;

  console.log('w3');
  // Create unified message for signing with domain, address and timestamps
  const message = JSON.stringify({
    domain,
    type: 'auth',
    address: algorandAddress,
    nonce,
    timestamp,
    network,
  });
  const messageBytes = new TextEncoder().encode(message);
  console.log('w4');

  // 2) First try to sign data directly through use-wallet-react
  let useTransactionFallback = false;

  try {
    console.log('q1');
    if (walletFunctions.signData) {
      console.log('q2');
      try {
        const signedData = await walletFunctions.signData([
          {
            address: algorandAddress,
            data: messageBytes,
            message,
          },
        ]);

        if (signedData && signedData.length > 0 && signedData[0].signature) {
          console.log('q3');
          const signatureB64 = toB64(signedData[0].signature);

          const [authToken] = (await apiRequest([
            {
              path: '/v1/auth/algorand/verify',
              method: 'POST',
              body: {
                method: 'signData',
                algorand_address: algorandAddress,
                signature: signatureB64,
                message,
                nonce,
                timestamp,
              },
            },
          ])) as [ApiResp<{ token: string }>];
          if (!authToken || authToken.isError) {
            throw new Error(authToken?.message || 'Failed to verify signature');
          }
          await accountStore.setAuthorizationToken(authToken.token);
          return;
        }
      } catch (signDataError: any) {
        console.log('q4');
        useTransactionFallback = true;
      }
    } else {
      console.log('q5');
      useTransactionFallback = true;
    }
  } catch (e: any) {
    console.log('q6');
    useTransactionFallback = true;
  }

  if (!useTransactionFallback) {
    console.log('q7');
    throw new Error('signData failed and no fallback triggered');
  }

  console.log('q8');
  // 3) Fallback: sign a dummy transaction (works with Ledger, Rekeyed and watch-only accounts)
  const algod = ensureAlgod(network);
  const sp = await algod.getTransactionParams().do();
  const suggestedParams = { ...sp, fee: 1000, flatFee: true };

  // Create a payment transaction with zero amount but with our message in the note field
  const unsignedTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
    sender: algorandAddress,
    receiver: algorandAddress, // send to ourselves
    amount: 0, // zero amount
    note: messageBytes, // server will extract and verify the message from the note field
    suggestedParams,
  });

  // Sign the transaction through use-wallet-react
  const encodedTxn = algosdk.encodeUnsignedTransaction(unsignedTxn);
  console.log('q9');

  // Handle cancellation via abort signal
  const signTransactionPromise = walletFunctions.signTransactions([encodedTxn]);

  // Create abort promise that rejects when signal is aborted
  const abortPromise = new Promise((_, reject) => {
    if (opts?.abortSignal) {
      opts.abortSignal.addEventListener('abort', () => {
        reject(new Error('USER_CANCELLED'));
      });
    }
  });

  // Also add a timeout as fallback
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('TIMEOUT')), 120000); // 120 second timeout
  });

  try {
    const signedTxns = await Promise.race([
      signTransactionPromise,
      abortPromise,
      timeoutPromise,
    ]);
    console.log('q10');

    if (!signedTxns || signedTxns.length === 0 || !signedTxns[0]) {
      console.log('q11');
      throw new Error('Wallet did not return signed transaction');
    }

    const signedTxnB64 = toB64(signedTxns[0]);

    // Send signed transaction to server for verification
    const [authToken] = (await apiRequest([
      {
        path: '/v1/auth/algorand/verify',
        method: 'POST',
        body: {
          method: 'txn', // specify transaction verification method
          algorand_address: algorandAddress,
          signedTxnB64, // pass the signed transaction in full
          message, // server will verify that the message matches the note in the transaction
          nonce,
          timestamp,
        },
      },
    ])) as [ApiResp<{ token: string }>];
    if (!authToken || authToken.isError) {
      throw new Error(authToken?.message || 'Failed to verify transaction');
    }
    console.log(
      'Algorand auth successful via transaction, setting token:',
      authToken.token.substring(0, 20) + '...',
    );
    await accountStore.setAuthorizationToken(authToken.token);
  } catch (signError: any) {
    console.log('q12');
    // Re-throw the error to be handled by the caller
    throw signError;
  }
}
